// src/components/DCData/DCData.js

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDCData,
  insertDCData,
  updateDCData,
  deleteDCData,
} from '../../../store/dcDataSlice';
import { fetchICTData } from '../../../store/uiSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../Styles/Common.css';

const DCData = () => {
  const dispatch = useDispatch();

  // Redux state
  const { dcData, loading, error } = useSelector((state) => state.dcData);
  const { ictData } = useSelector((state) => state.ui);

  // Local state for search, pagination, and popup form
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // DC form data
  const [formData, setFormData] = useState({
    id: '',
    ict_id: '',
    dcCost: '',
    dcName: '',
    dcType: 'inHouse',
    dcURL: '',
    dcUName: '',
    dcUPass: '',
    dcTokenPass: '',
    status: 'A',
  });

  // For pagination, 10 items per page
  const itemsPerPage = 10;

  // Fetch DC data & ICT data on mount
  useEffect(() => {
    dispatch(fetchDCData());
    dispatch(fetchICTData());
  }, [dispatch]);

  // --------------- Handlers ---------------

  // Controlled inputs in the form
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Submit the form for adding/editing
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await dispatch(updateDCData(formData)).unwrap();
        toast.success('DC Data updated successfully!');
      } else {
        await dispatch(insertDCData(formData)).unwrap();
        toast.success('DC Data inserted successfully!');
      }
      closeForm();
    } catch (err) {
      toast.error('Failed to save DC Data.');
    }
  };

  // Delete record
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await dispatch(deleteDCData(id)).unwrap();
        toast.success('Record deleted successfully!');
      } catch (err) {
        toast.error('Failed to delete record.');
      }
    }
  };

  // Open form in edit mode
  const handleEdit = (dc) => {
    setEditMode(true);
    setIsFormOpen(true);
    setFormData({ ...dc });
  };

  // Open form in add mode
  const handleAdd = () => {
    setEditMode(false);
    setIsFormOpen(true);
    setFormData({
      id: '',
      ict_id: '',
      dcCost: '',
      dcName: '',
      dcType: 'inHouse',
      dcURL: '',
      dcUName: '',
      dcUPass: '',
      dcTokenPass: '',
      status: 'A',
    });
  };

  // Close and reset form
  const closeForm = () => {
    setIsFormOpen(false);
    setFormData({
      id: '',
      ict_id: '',
      dcCost: '',
      dcName: '',
      dcType: 'inHouse',
      dcURL: '',
      dcUName: '',
      dcUPass: '',
      dcTokenPass: '',
      status: 'A',
    });
  };

  // Toggle status via switch
  const handleToggleStatus = async (dcItem) => {
    const newStatus = dcItem.status === 'A' ? 'D' : 'A';
    const updated = { ...dcItem, status: newStatus };

    try {
      await dispatch(updateDCData(updated)).unwrap();
      toast.success(`Status updated to ${newStatus === 'A' ? 'Active' : 'Deactivated'}`);
    } catch (err) {
      toast.error('Failed to update status.');
    }
  };

  // --------------- Filtering & Pagination ---------------

  // Filter by dcName
  const filteredDCData = dcData.filter((item) =>
    item.dcName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination: current page
  const totalPages = Math.ceil(filteredDCData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDCData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="container">
      <div className="header-container">
        <h2>DC Data</h2>
        <button className="add-lead-btn" onClick={handleAdd}>
          <i className="fa fa-plus"></i>
        </button>
      </div>

      <div className="controls">
        <input
          type="text"
          placeholder="Search by DC Name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
      </div>

      {loading && <p>Loading DC data...</p>}
      {error && <p className="error">{error}</p>}

      {/* Table */}
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>ICT Name</th>
            <th>Name</th>
            <th>Type</th>
            <th>Cost</th>
            <th>URL</th>
            <th>User Name</th>
            <th>Password</th>
            <th>TokenPass</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((dc, index) => {
            // Map dc.ict_id to its name from ictData
            const ictItem = ictData.find((ict) => ict.id === dc.ict_id);
            const ictName = ictItem ? ictItem.ictName : `ICT #${dc.ict_id}`;

            return (
              <tr key={dc.id}>
                <td>{index + 1}</td>
                <td>{ictName}</td>
                <td>{dc.dcName}</td>
                <td>{dc.dcType}</td>
                <td>{dc.dcCost}</td>
                <td>{dc.dcURL}</td>
                <td>{dc.dcUName}</td>
                <td>{dc.dcUPass}</td>
                <td>{dc.dcTokenPass}</td>
                <td>
                  {/* Toggle switch for status */}
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={dc.status === 'A'}
                      onChange={() => handleToggleStatus(dc)}
                    />
                    <span className="slider round"></span>
                  </label>
                </td>
                <td>
                  <button
                    className="icon-btn update-btn"
                    onClick={() => handleEdit(dc)}
                  >
                    <i className="fa fa-edit" aria-hidden="true"></i>
                  </button>
                  <button
                    className="icon-btn delete-btn"
                    onClick={() => handleDelete(dc.id)}
                  >
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={currentPage === i + 1 ? 'active' : ''}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Popup form */}
      {isFormOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? 'Edit DC Data' : 'Add DC Data'}</h3>
            <form onSubmit={handleSubmit}>
              {/* DC Name */}
              <div className="form-group">
                <label>DC Name</label>
                <input
                  type="text"
                  name="dcName"
                  value={formData.dcName}
                  onChange={handleChange}
                  required
                />
              </div>
              {/* DC Type */}
              <div className="form-group">
                <label>DC Type</label>
                <select
                  name="dcType"
                  value={formData.dcType}
                  onChange={handleChange}
                >
                  <option value="inHouse">inHouse</option>
                  <option value="freelanced">freelanced</option>
                  <option value="tp">tp</option>
                  <option value="cobranded">cobranded</option>
                </select>
              </div>
              {/* DC Cost */}
              <div className="form-group">
                <label>DC Cost</label>
                <input
                  type="number"
                  step="0.01"
                  name="dcCost"
                  placeholder="Enter Amount"
                  value={formData.dcCost}
                  onChange={handleChange}
                  required
                />
              </div>
              {/* ICT Name (dropdown) */}
              <div className="form-group">
                <label>ICT Name</label>
                <select
                  name="ict_id"
                  value={formData.ict_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select ICT --</option>
                  {ictData.map((ict) => (
                    <option key={ict.id} value={ict.id}>
                      {ict.ictName}
                    </option>
                  ))}
                </select>
              </div>
              {/* DC URL */}
              <div className="form-group">
                <label>DC URL</label>
                <input
                  type="text"
                  name="dcURL"
                  value={formData.dcURL}
                  onChange={handleChange}
                  required
                />
              </div>
              {/* DC User Name */}
              <div className="form-group">
                <label>DC User Name</label>
                <input
                  type="text"
                  name="dcUName"
                  value={formData.dcUName}
                  onChange={handleChange}
                  required
                />
              </div>
              {/* DC Password */}
              <div className="form-group">
                <label>DC Password</label>
                <input
                  type="text"
                  name="dcUPass"
                  value={formData.dcUPass}
                  onChange={handleChange}
                  required
                />
              </div>
              {/* DC Token Password */}
              <div className="form-group">
                <label>DC TokenPass</label>
                <input
                  type="text"
                  name="dcTokenPass"
                  value={formData.dcTokenPass}
                  onChange={handleChange}
                  required
                />
              </div>
              {/* Status */}
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="A">Active (A)</option>
                  <option value="D">Deactivated (D)</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="fa fa-check submit-icon"></button>
                <button
                  type="button"
                  className="fa fa-times cancel-icon"
                  onClick={closeForm}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default DCData;
